import { HubSearchResult } from '../../types/hubSearch';
import { Activity, Contact, CRMUser, Deal, Lead } from '../../types';
import { searchCRM, scoreMultiFieldMatch } from '../search';
import { formatSourceName } from '../sourceUtils';

export interface SearchLeadsHubOptions {
  query: string;
  leads: Lead[];
  contacts?: Contact[];
  deals?: Deal[];
  activities?: Activity[];
  crmUsers?: CRMUser[];
  limit?: number;
}

function leadTitle(lead: Lead): string {
  return `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unnamed Lead';
}

function leadSubtitle(lead: Lead, crmUsers: CRMUser[]): string {
  const owner = lead.addedBy
    ? crmUsers.find(u => u.uid === lead.addedBy)?.displayName
    : undefined;
  return [
    lead.email,
    lead.location,
    formatSourceName(lead.source),
    owner ? `Owner: ${owner}` : undefined,
  ].filter(Boolean).join(' · ');
}

function crmToHub(r: ReturnType<typeof searchCRM>[number]): HubSearchResult {
  return {
    id: r.id,
    kind: r.type,
    section: r.type === 'lead' ? 'Leads' : r.type === 'contact' ? 'Contacts' : 'Deals',
    title: r.title,
    subtitle: r.subtitle,
    matchScore: r.matchScore,
    payload: r.entity,
  };
}

export function searchLeadsHub(options: SearchLeadsHubOptions): HubSearchResult[] {
  const {
    query,
    leads,
    contacts = [],
    deals = [],
    activities = [],
    crmUsers = [],
    limit = 24,
  } = options;

  const q = query.trim();
  if (!q) {
    return leads
      .filter(l => l.id && !l.archived)
      .slice(0, 12)
      .map(lead => ({
        id: lead.id!,
        kind: 'lead' as const,
        section: 'Leads',
        title: leadTitle(lead),
        subtitle: leadSubtitle(lead, crmUsers),
        matchScore: 1,
        payload: lead,
      }));
  }

  const results: HubSearchResult[] = [];
  const seenLeadIds = new Set<string>();
  const seenContactIds = new Set<string>();
  const seenDealIds = new Set<string>();

  for (const r of searchCRM({ query: q, leads, contacts, deals, limit: limit * 2 })) {
    const hub = crmToHub(r);
    results.push(hub);
    if (r.type === 'lead') seenLeadIds.add(r.id);
    if (r.type === 'contact') seenContactIds.add(r.id);
    if (r.type === 'deal') seenDealIds.add(r.id);
  }

  // Boost / add leads from activity matches, labels, owner names
  for (const lead of leads) {
    if (!lead.id || seenLeadIds.has(lead.id)) continue;
    const ownerName = lead.addedBy
      ? crmUsers.find(u => u.uid === lead.addedBy)?.displayName
      : undefined;
    const labelsText = (lead.labels || []).join(' ');
    let score = scoreMultiFieldMatch(q, [
      { text: labelsText, weight: 6 },
      { text: ownerName, weight: 5 },
    ]);

    const relatedActivities = activities.filter(
      a => a.entityType === 'lead' && a.entityId === lead.id,
    );
    for (const act of relatedActivities) {
      const actScore = scoreMultiFieldMatch(q, [
        { text: act.title, weight: 4 },
        { text: act.description, weight: 3 },
        { text: act.outcome, weight: 2 },
      ]);
      score = Math.max(score, actScore);
    }

    if (score > 0) {
      seenLeadIds.add(lead.id);
      results.push({
        id: lead.id,
        kind: 'lead',
        section: 'Leads',
        title: leadTitle(lead),
        subtitle: leadSubtitle(lead, crmUsers),
        matchScore: score,
        payload: lead,
      });
    }
  }

  return results
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
}
